import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Fragment, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeFuncErrorEvent } from '../../common/AdobeTagging/adobeFuncErrorEvent';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { PortalIdentifier } from '../../common/Portal';
import { PSXHeader } from '../../common/PSXHeader';
import { StillNeedHelp } from '../../common/StillNeedHelp';
import { getFeatureFlag } from '../../common/Utils';
import { PortalContext } from '../../context/PortalContext';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useLagoon } from '../../hooks/useLagoon';
import { isGuestFlow } from '../../utils/error.utils';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { getCurrentMember } from '../../utils/user.utils';
import { Icon } from './Icon';
import { IconContainer, PageContainer, PageContent } from './Page404.style';

export const Page404 = () => {
  const { t } = useTranslation();
  const breadcrumbs = [{ title: t('ERROR_PAGE.404.title'), href: '' }];
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    is404: true,
    pageName: 'error 404',
  });

  const currentMember = getCurrentMember();
  const { getRouteParams } = useRouter();
  const { token } = getRouteParams();
  const isGuest = isGuestFlow();
  const tokenData = tokenizer.parse(token) || {};
  const { message, code, source, uiSection } = tokenData;

  const { portalData } = useContext(PortalContext);
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  const suppressPsxLinks: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PSX_LINKS_ON_MEMBER_INFO_FAILURE
  );

  useEffect(() => {
    adobeFuncErrorEvent({
      message,
      source,
      code: code ?? '404',
      uiSection,
    });
    adobePageTrackEvent();
  }, []);

  return (
    <Fragment>
      <PSXHeader
        breadcrumbs={breadcrumbs}
        dataTestId="error-404-search-form"
        showChips={false}
        showChoosePCPHeader={false}
        showDemoHeader={false}
        showMemberSelection={false}
      />
      <PageContainer>
        {!isGehaTheme && (
          <IconContainer>
            <Icon />
          </IconContainer>
        )}
        <PageContent>
          <Text
            color="#C40000"
            css={{
              'abyss-text-root': {
                marginTop: isGehaTheme ? '100px' : '34px',
                marginBottom: '4px',
                '@media (max-width: 464px)': {
                  marginBottom: '29px',
                  marginTop: 'unset',
                },
                display: 'block',
              },
            }}
            fontSize="xs"
            fontWeight="bold"
          >
            {t('ERROR_PAGE.404.type')}
          </Text>
          <Heading
            css={{
              marginBottom: '4px',
              '@media (max-width: 464px)': {
                marginBottom: '16px',
              },
            }}
            level={1}
            size="lg"
          >
            {t('ERROR_PAGE.404.heading')}
          </Heading>
          <Heading
            css={{
              marginBottom: '12px',
              '@media (max-width: 464px)': {
                marginBottom: '20px',
                maxWidth: '181px',
              },
            }}
            level={6}
            size="xs"
          >
            {t('ERROR_PAGE.404.subHeading')}
          </Heading>
          {!(suppressPsxLinks && !currentMember) && (
            <Text fontSize="sm">{t('ERROR_PAGE.404.body1')}</Text>
          )}
        </PageContent>
      </PageContainer>
      {!isGuest && <StillNeedHelp />}
    </Fragment>
  );
};
